import { ApiError, fetchWithAuth } from "@entities/user-tokens";

import type { Project } from "../Project";

/** 1 リクエストで取り切りやすい最大ページサイズ（サーバー上限＝100）。 */
const PAGE_LIMIT = 100;

type ProjectsPage = {
    projects: Project[];
    pageInfo: { nextCursor: string | null; hasMore: boolean };
};

/**
 * 案件一覧を取得する (updatedAt 降順)。
 *
 * `GET /projects` はカーソルページング（既定 limit=50）であり、選択肢の母集合としては
 * 先頭ページだけでは足りない。`hasMore` が尽きるまでカーソルを辿って全ページを連結する。
 * サーバーがカーソルを進めない異常時に備え、同一カーソルの再出現で打ち切る。
 */
export async function listProjects(): Promise<Project[]> {
    const all: Project[] = [];
    const seenCursors = new Set<string>();
    let cursor: string | null = null;
    for (;;) {
        const page = await fetchPage(cursor);
        all.push(...page.projects);
        const next = page.pageInfo.nextCursor;
        if (!page.pageInfo.hasMore || next === null) break;
        if (seenCursors.has(next)) break;
        seenCursors.add(next);
        cursor = next;
    }
    return all;
}

async function fetchPage(cursor: string | null): Promise<ProjectsPage> {
    const qs = new URLSearchParams({ limit: String(PAGE_LIMIT) });
    if (cursor) qs.set("cursor", cursor);
    const res = await fetchWithAuth(`/projects?${qs.toString()}`, {
        headers: { Accept: "application/json" },
    });
    if (!res.ok) throw await ApiError.fromResponse(res, "案件一覧の取得に失敗しました");
    return (await res.json()) as ProjectsPage;
}
